import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Lock, Phone } from "lucide-react";
import api from "@/lib/api";
import { LoadingState } from "@/components/ui/States";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const money = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function PayInvoice() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    useDocumentTitle("Pay Your Invoice · A1 Screening", "Secure online payment for your A1 Screening invoice.");

    useEffect(() => {
        api.get(`/pay/${token}`)
            .then((r) => setInfo(r.data))
            .catch(() => setNotFound(true));
    }, [token]);

    const pay = async () => {
        setBusy(true);
        setError("");
        try {
            const r = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't start the payment. Please try again or call us.");
            setBusy(false);
        }
    };

    return (
        <div className="a1-container a1-section max-w-lg" data-testid="pay-invoice-page">
            {notFound ? (
                <div className="a1-card text-center py-10" data-testid="pay-invalid">
                    <h1 className="text-2xl mb-3">This payment link isn't valid</h1>
                    <p className="text-sm mb-5" style={{ color: "var(--color-text-muted)" }}>
                        It may have been replaced. Please call us and we'll sort it out right away.
                    </p>
                    <a href="tel:+12394347306" className="a1-btn-primary inline-flex">
                        <Phone size={16} /> Call 239-434-7306
                    </a>
                </div>
            ) : !info ? (
                <LoadingState label="Loading your invoice…" />
            ) : info.paid ? (
                <div className="a1-card text-center py-10" data-testid="pay-already-paid">
                    <ShieldCheck size={36} className="mx-auto mb-3" style={{ color: "var(--color-accent)" }} />
                    <h1 className="text-2xl mb-2">This invoice is paid — thank you{info.first_name ? `, ${info.first_name}` : ""}!</h1>
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                        Nothing more to do. Questions? Call us at 239-434-7306.
                    </p>
                </div>
            ) : (
                <div className="a1-card a1-card-feature" data-testid="pay-invoice-card">
                    <p className="a1-eyebrow mb-2">A1 Screening · Invoice #{info.reference}</p>
                    <h1 className="text-3xl mb-1">
                        {info.first_name ? `Hi ${info.first_name} — ` : ""}your invoice is ready
                    </h1>
                    {info.service ? (
                        <p className="text-sm mb-4" style={{ color: "var(--color-text-muted)" }}>{info.service}</p>
                    ) : null}
                    <p className="text-5xl font-bold my-5" style={{ color: "var(--color-text)" }} data-testid="pay-amount">
                        {money(info.amount)}
                    </p>
                    {error ? (
                        <p className="text-sm mb-3" style={{ color: "var(--color-danger)" }} data-testid="pay-error">{error}</p>
                    ) : null}
                    <button
                        type="button"
                        onClick={pay}
                        disabled={busy}
                        className="a1-btn-primary w-full justify-center"
                        data-testid="pay-now-btn"
                    >
                        <Lock size={16} /> {busy ? "Opening secure checkout…" : "Pay securely with Stripe"}
                    </button>
                    <p className="text-xs mt-4 flex items-center gap-1.5" style={{ color: "var(--color-text-dim)" }}>
                        <ShieldCheck size={13} /> Payment happens on Stripe's secure page — A1 Screening never sees your card details.
                    </p>
                </div>
            )}
        </div>
    );
}
